import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { useToast } from "@/hooks/use-toast";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardPromotionalCards } from "@/components/dashboard/DashboardPromotionalCards";
import { usePolicyData } from "@/hooks/usePolicyData";
import { UserPolicyCard } from "@/components/dashboard/UserPolicyCard";
import { Spinner } from "@/components/ui/spinner";
import { Policy } from "@/components/admin/contact-details/sections/policy/types";

const MyPoliciesPage = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const { policies, loading, error } = usePolicyData(userId);

  useEffect(() => {
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to get session. Please try logging in again."
          });
          return;
        }

        if (session?.user) {
          setUserId(session.user.id);
        } else {
          toast({
            variant: "destructive",
            title: "Not Logged In",
            description: "Please log in to view your policies."
          });
        }
      } catch (error) {
        console.error("Error getting session:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again."
        });
      }
    };

    getSession();
  }, [toast]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-red-500">
          An error occurred while fetching your policies. Please try again later.
        </div>
      );
    }

    if (!policies || policies.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          You don't have any active policies at this time.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {(policies as Policy[]).map((policy) => (
          <UserPolicyCard key={policy.id} policy={policy} />
        ))}
      </div>
    );
  };

  return (
    <PageLayout showBreadcrumbs={false}>
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1 p-8">
            <h1 className="text-3xl font-bold mb-8">My Policies</h1>
            {userId ? (
              <div className="space-y-8">
                {renderContent()}
                <DashboardPromotionalCards />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Please sign in to view your policies.
              </div>
            )}
          </div>
        </div>
      </SidebarProvider>
    </PageLayout>
  );
};

export default MyPoliciesPage;